import api from "../../Environment";
import { getGuestToken, persistGuestToken } from "./commerceApi";

/**
 * A-009 — Checkout / Order API. React consumes only checkoutPayload / orderPayload.
 */

const CHECKOUT_ID_KEY = "commerce_checkout_id";

export function getCheckoutId() {
  try {
    return localStorage.getItem(CHECKOUT_ID_KEY) || "";
  } catch (e) {
    return "";
  }
}

export function persistCheckoutId(id) {
  if (!id) return;
  try {
    localStorage.setItem(CHECKOUT_ID_KEY, id);
  } catch (e) {
    /* ignore */
  }
}

export function clearCheckoutId() {
  try {
    localStorage.removeItem(CHECKOUT_ID_KEY);
  } catch (e) {
    /* ignore */
  }
}

function withGuest(params = {}) {
  const guest = getGuestToken();
  if (guest) {
    return { ...params, guest_token: guest };
  }
  return { ...params };
}

function unwrap(response, label) {
  const body = response && response.data;
  if (!body || body.success === false) {
    const err = new Error(
      (body && (body.error_message || body.error_messages)) ||
        `${label} failed`
    );
    err.code = body && body.error_code;
    throw err;
  }
  const data = body.data || {};
  if (data.cart && data.cart.guest_token) {
    persistGuestToken(data.cart.guest_token);
  }
  if (data.checkout_id) {
    persistCheckoutId(data.checkout_id);
  }
  return data;
}

function encodeObject(obj) {
  if (!obj || typeof obj !== "object") return "";
  return JSON.stringify(obj);
}

export async function createCheckout(seed = {}) {
  const params = withGuest({});
  if (seed.customer) params.customer = encodeObject(seed.customer);
  if (seed.billing) params.billing = encodeObject(seed.billing);
  if (seed.shipping) params.shipping = encodeObject(seed.shipping);
  const response = await api.postMethod("commerce/checkout/create", params);
  return unwrap(response, "Create checkout");
}

export async function fetchCheckout(checkoutId) {
  const id = checkoutId || getCheckoutId();
  const response = await api.postMethod(
    "commerce/checkout",
    withGuest({ checkout_id: id })
  );
  return unwrap(response, "Load checkout");
}

export async function updateCheckoutBilling(checkoutId, billing) {
  const response = await api.postMethod(
    "commerce/checkout/billing",
    withGuest({
      checkout_id: checkoutId || getCheckoutId(),
      billing: encodeObject(billing),
    })
  );
  return unwrap(response, "Update billing");
}

export async function updateCheckoutShipping(checkoutId, shipping) {
  const response = await api.postMethod(
    "commerce/checkout/shipping",
    withGuest({
      checkout_id: checkoutId || getCheckoutId(),
      shipping: encodeObject(shipping),
    })
  );
  return unwrap(response, "Update shipping");
}

export async function validateCheckout(checkoutId) {
  const response = await api.postMethod(
    "commerce/checkout/validate",
    withGuest({ checkout_id: checkoutId || getCheckoutId() })
  );
  return unwrap(response, "Validate checkout");
}

export async function createOrder(checkoutId) {
  const response = await api.postMethod(
    "commerce/order/create",
    withGuest({ checkout_id: checkoutId || getCheckoutId() })
  );
  const data = unwrap(response, "Create order");
  clearCheckoutId();
  return data;
}

export async function chargeOrder(orderNumber, cardToken, idempotencyKey) {
  const params = withGuest({
    order_number: orderNumber,
    card_token: cardToken,
  });
  if (idempotencyKey) {
    params.idempotency_key = idempotencyKey;
  }
  const response = await api.postMethod("commerce/order/charge", params);
  return unwrap(response, "Charge order");
}

export async function fetchOrder(orderNumber) {
  const response = await api.postMethod(
    "commerce/order",
    withGuest({ order_number: orderNumber })
  );
  return unwrap(response, "Load order");
}
